import {type IFilterContext, useFilterContext} from 'context/FilterContext';
import {type IFilter} from 'helpers/filters/action';
import {filterSites} from 'helpers/filters/site';
import {dateReader} from 'helpers/UnitsFormatHandler';
import {useGetPlan, useGetRegions, useGetSites} from './swr';
import {type Site} from 'client';
import {URLPathname} from '../interfaces';

export const useGetFilteredSites = ({
  planId,
  pathname,
}: {
  planId: string;
  pathname: string;
}): {filterContext: IFilterContext; data: Site[]; filteredSitesNames: string[]} => {
  const {data: plan} = useGetPlan({resourceId: planId});
  const filterContext = useFilterContext();
  const {data: sites} = useGetSites({resourceId: planId});
  const {data: regions} = useGetRegions({resourceId: planId});

  if (!sites || !regions || !plan) {
    return {filterContext, data: [], filteredSitesNames: []};
  }

  const selectedRegions = filterContext?.selectedRegions;
  const startDate = dateReader(plan.startDate);
  const endDate = dateReader(plan.endDate);

  const siteFilters = getFilterByView(filterContext, pathname);
  const filter: IFilter = {
    actionTypes: [],
    regions: selectedRegions ?? [],
    siteGroups: siteFilters.siteGroups,
    sites: siteFilters.sites,
    cages: [],
    species: [],
    certificates: [],
    inputWeightBins: [],
    binSize: 0,
    startDate,
    endDate,
    harvestWeightClasses: [],
    harvestQualityClasses: [],
    harvestPlants: [],
    packagingCodes: [],
  };

  const filteredSites = filterSites({filter, sites, regions});

  return {
    data: filteredSites,
    filteredSitesNames: filteredSites.map((site) => site.name),
    filterContext,
  };
};

const getFilterByView = (filterContext: IFilterContext, pathname: string): {siteGroups: string[]; sites: string[]} => {
  switch (pathname) {
    case URLPathname.organizationOverview:
      return {siteGroups: [], sites: filterContext?.siteOverviewSelectedSites ?? []};
    case URLPathname.planOverview:
      return {siteGroups: [], sites: filterContext?.overviewSelectedSites ?? []};
    case URLPathname.overviewTable:
      return {
        siteGroups: filterContext?.overviewTableSelectedSiteGroups ?? [],
        sites: filterContext?.overviewTableSelectedSites ?? [],
      };
    case URLPathname.planning:
      return {
        siteGroups: filterContext?.longTermViewSelectedSiteGroups ?? [],
        sites: filterContext?.longTermViewSelectedSites ?? [],
      };
    default:
      return {siteGroups: [], sites: []};
  }
};
